import { getAuth, sendPasswordResetEmail, fetchSignInMethodsForEmail } from 'firebase/auth';

/**
 * Send password reset email to user
 * @param {string} email - User's email address
 * @returns {Promise<void>}
 */
export const sendPasswordReset = async (email) => {
  try {
    const auth = getAuth();
    
    // First check if the user exists in Firebase Auth
    const signInMethods = await fetchSignInMethodsForEmail(auth, email);
    
    if (signInMethods.length === 0) {
      return { 
        success: false, 
        message: 'No Firebase Auth account found for this email. User may need to be created in Firebase Auth first.' 
      };
    }
    
    await sendPasswordResetEmail(auth, email);
    return { success: true, message: 'Password reset email sent successfully' };
  } catch (error) {
    console.error('Error sending password reset email:', error);
    
    switch (error.code) {
      case 'auth/user-not-found':
        return { success: false, message: 'No user found with this email address' };
      case 'auth/invalid-email':
        return { success: false, message: 'Invalid email address' };
      case 'auth/too-many-requests':
        return { success: false, message: 'Too many requests. Please try again later' };
      default:
        return { success: false, message: 'Failed to send password reset email' };
    }
  }
};

/**
 * Create Firebase Auth account for manager and send password reset
 * @param {string} email - User's email address
 * @param {string} password - Temporary password
 * @returns {Promise<Object>}
 */
export const createAuthAccountAndSendReset = async (email, password) => {
  try {
    console.log('Starting password reset process for:', email);
    const auth = getAuth();
    
    // First check if the user already exists in Firebase Auth
    console.log('Checking if user exists in Firebase Auth...');
    const signInMethods = await fetchSignInMethodsForEmail(auth, email);
    console.log('Sign-in methods found:', signInMethods);
    
    if (signInMethods.length > 0) {
      // User already exists, just send password reset
      console.log('User exists in Firebase Auth, sending password reset email...');
      await sendPasswordResetEmail(auth, email);
      console.log('Password reset email sent successfully to existing user');
      return { 
        success: true, 
        message: 'Password reset email sent successfully to existing user' 
      };
    }
    
    // User doesn't exist, create account first
    console.log('User does not exist in Firebase Auth, creating account...');
    const { createUserWithEmailAndPassword } = await import('firebase/auth');
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    console.log('Firebase Auth account created successfully:', userCredential.user.uid);
    
    // Send password reset email to the newly created user
    console.log('Sending password reset email to newly created user...');
    await sendPasswordResetEmail(auth, email);
    console.log('Password reset email sent successfully to new user');
    
    return { 
      success: true, 
      message: 'Firebase Auth account created and password reset email sent successfully' 
    };
  } catch (error) {
    console.error('Error creating auth account and sending reset:', error);
    
    switch (error.code) {
      case 'auth/email-already-in-use':
        console.log('Email already in use, attempting to send password reset anyway...');
        try {
          const auth = getAuth();
          await sendPasswordResetEmail(auth, email);
          return { 
            success: true, 
            message: 'Email already in use. Password reset email sent successfully.' 
          };
        } catch (resetError) {
          console.error('Error sending password reset to existing user:', resetError);
          return { 
            success: false, 
            message: 'Email already in use but failed to send password reset email.' 
          };
        }
      case 'auth/invalid-email':
        return { success: false, message: 'Invalid email address' };
      case 'auth/weak-password':
        return { success: false, message: 'Password is too weak' };
      default:
        return { success: false, message: `Failed to create account and send reset email: ${error.message}` };
    }
  }
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {Object} - Validation result with isValid and message
 */
export const validatePasswordStrength = (password) => {
  const minLength = 6;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  if (password.length < minLength) {
    return {
      isValid: false,
      message: `Password must be at least ${minLength} characters long`
    };
  }

  if (!hasUpperCase || !hasLowerCase) {
    return {
      isValid: false,
      message: 'Password must contain both uppercase and lowercase letters'
    };
  }

  if (!hasNumbers) {
    return {
      isValid: false,
      message: 'Password must contain at least one number'
    };
  }

  if (!hasSpecialChar) {
    return {
      isValid: false,
      message: 'Password must contain at least one special character'
    };
  }

  return {
    isValid: true,
    message: 'Password meets security requirements'
  };
};

/**
 * Generate a random password
 * @param {number} length - Length of password (default: 12)
 * @returns {string} - Generated password
 */
export const generateRandomPassword = (length = 12) => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  let password = '';
  
  // Ensure at least one character from each category
  password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // Uppercase
  password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // Lowercase
  password += '0123456789'[Math.floor(Math.random() * 10)]; // Number
  password += '!@#$%^&*'[Math.floor(Math.random() * 8)]; // Special char
  
  // Fill the rest randomly
  for (let i = 4; i < length; i++) {
    password += charset[Math.floor(Math.random() * charset.length)];
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
}; 